// src/frontend/js/modern/core/ui-manager.js
//
// UIManager orchestrates generic UI widgets (tooltip, dropdown, popup,
// sortable, layout, image-fix). Module-specific state application is
// delegated to each module's own applyState() via BaseModule.
import {
	TooltipHandler,
	DropdownHandler,
	PopupHandler,
	SortableHandler,
	LayoutHandler,
	ImageFixHandler
} from '../ui';
import { eventBus, EVENTS } from './event-bus.js';
import { DropdownLite } from '../lib/dropdown-lite.js';

class UIManager {
	constructor() {
		this.tooltip = new TooltipHandler();
		this.dropdown = new DropdownHandler();
		this.popup = new PopupHandler();
		this.sortable = new SortableHandler();
		this.layout = new LayoutHandler();
		this.imageFix = new ImageFixHandler();
		this._initialized = false;
		this._reinitializing = false;
		this._listsDropdown = null;
		this._premium = false;
	}

	init(options = {}) {
		if (this._initialized) return;
		this.tooltip.init();
		this.dropdown.init();
		this.popup.init();
		this.imageFix.init();
		this._premium = options.premium || false;
		if (this._premium) {
			this.sortable.init();
			this.layout.init();
			this._initDropdownLists();
		}
		eventBus.on(EVENTS.CONTENT_UPDATED, () => {
			if (this._premium) {
				this._initDropdownLists();
			}
		});
		eventBus.on('ui:reinit', () => this.reInit());
		this._initialized = true;
		console.log('[UIManager] Initialized');
	}

	destroy() {
		this.tooltip.destroy?.();
		this.dropdown.destroy?.();
		this.popup.destroy?.();
		this.imageFix.destroy?.();
		if (this._premium) {
			this.sortable.destroy?.();
			this.layout.destroy?.();
		}
		this._initialized = false;
		if (this._listsDropdown) {
			this._listsDropdown.destroy();
			this._listsDropdown = null;
		}
		console.log('[UIManager] Destroyed');
	}

	reInit() {
		if (this._reinitializing) return;
		this._reinitializing = true;
		try {
			const opts = { premium: this._premium };
			this.destroy();
			this.init(opts);
		} finally {
			this._reinitializing = false;
		}
	}

	/**
	 * Initialize the premium "My Lists" dropdown widget.
	 * Called on init and after every CONTENT_UPDATED event.
	 */
	_initDropdownLists() {
		const dropdownElement = document.getElementById('wlfmc_my_lists');
		if (!dropdownElement) return;
		if (this._listsDropdown) {
			this._listsDropdown.destroy();
			this._listsDropdown = null;
		}
		this._listsDropdown = new DropdownLite(dropdownElement, {
			effects: 'slide',
		});
		dropdownElement.addEventListener('select.editable-select', (e) => {
			const li = e.detail;
			if (!li) return;
			const wrapper = dropdownElement.closest('.wlfmc-dropdown-wrapper');
			if (!wrapper) return;
			wrapper.classList.add('wlfmc-action', 'wlfmc-loading-alt', 'wlfmc-inverse');
			const wishlistId = li.getAttribute('value') || li.value || '';
			const target = wrapper.querySelector(`.list-item[data-wishlist-id="${wishlistId}"]`);
			if (!target) return;
			const listName = target.querySelector('.list-name')?.textContent || '';
			dropdownElement.value = listName;
			const url = target.getAttribute('data-url') || target.dataset.url;
			if (url) window.location.href = url;
		});
	}
}

export const uiManager = new UIManager();
export default uiManager;
